import { APIFactory, BaseController } from 'controllers/BaseController'
import moment from 'moment'
import { ErrorMessage } from 'store/Application/appActions'
import { AnalyticsAPI } from './AnalyticsAPI'
import { QuantityDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { KPITargetConfiguration } from 'components/Dashboard/DashboardWidget'

export interface SalesLeadTimeDataset {
  date: string
  projectcount: number
  leadtime: number
  unixtimeinseconds: number
  targetLeadTime?: number
}

export interface ConvertionRateDataset {
  conversion_rate: number
}

export enum KPI {
  responseTime = 'ResponseTime',
  negotiationTime = 'NegotiationTime',
  salesLeadtime = 'SalesLeadtime',
  conversionRate = 'ConversionRate',
}

export type GetKPIConfigurationsResponse = {
  configurations: Array<KPITargetConfiguration>
}

export interface IAnalyticsAPI {
  GetSalesLeadTime: (
    organizationId: string,
    startDate: string,
    endDate: string,
    fromState: string,
    toState: string
  ) => Promise<SalesLeadTimeDataset[]>
  GetConversionRate: (
    organizationId: string,
    startDate: string,
    endData: string
  ) => Promise<ConvertionRateDataset>
  GetKPIConfigurations: (
    organizationId: string
  ) => Promise<KPITargetConfiguration[]>
  SetKPITarget: (
    organizationId: string,
    kpi: KPI,
    value: QuantityDto
  ) => Promise<void>
}

export type KpiFilterAndTargetConfiguration = {
  startDate: string | 'month' | 'quarter' | Date
  endDate: string | 'today' | 'yesterday' | 'weekago' | Date
  value: number
  unit: 'seconds' | 'minutes' | 'hours' | 'days' | '%'
}

export class SalesLeadTimeController extends BaseController<IAnalyticsAPI> {
  private LOCAL_STORAGE_KEY = `${this.organizationId}-SalesLeadTimeGraph-`
  private configurationCache: KpiFilterAndTargetConfiguration

  public SalesLeadTimeData: SalesLeadTimeDataset[] = undefined

  constructor(
    private organizationId,
    graphLocalStorageKeySuffix: string,
    targetValue: number,
    targetUnit: 'seconds' | 'minutes' | 'hours' | 'days',
    private kpi: KPI,
    apiFactory?: APIFactory<IAnalyticsAPI>
  ) {
    super(
      apiFactory
        ? apiFactory
        : (onRequestChange) => new AnalyticsAPI(onRequestChange)
    )
    this.LOCAL_STORAGE_KEY += graphLocalStorageKeySuffix

    this.SetFilterConfiguration({
      ...this.configurationCache,
      value: targetValue,
      unit: targetUnit,
      startDate: 'month',
      endDate: 'today',
    })
  }

  public GetGraphConfiguration(): KpiFilterAndTargetConfiguration {
    try {
      if (this.configurationCache) return this.configurationCache

      return this.SetFilterConfiguration(
        JSON.parse(localStorage.getItem(this.LOCAL_STORAGE_KEY)) ?? {
          startDate: 'month',
          endDate: 'today',
          value: 2,
          unit: 'days',
        }
      )
    } catch (err) {
      this.LogError(err)
      ErrorMessage('GetConfigurationFilter', err)
      return null
    }
  }

  public async SaveTargetConfiguration(value: number, unit: string) {
    await this.api.SetKPITarget(this.organizationId, this.kpi, {
      value,
      unit,
    })
  }

  public SetFilterConfiguration(
    configuration: KpiFilterAndTargetConfiguration
  ) {
    this.configurationCache = configuration

    try {
      localStorage.setItem(
        this.LOCAL_STORAGE_KEY,
        JSON.stringify(configuration)
      )

      return configuration
    } catch (err) {
      this.LogError(err)
      ErrorMessage('SetConfigurationFilter', err)
      return null
    }
  }

  public async SetTargetConfiguration(kpi: KPI, value: QuantityDto) {
    if (!value?.value) {
      return
    }

    await this.api.SetKPITarget(this.organizationId, kpi, value)
    this.configurationCache = {
      ...this.configurationCache,
      value: value.value,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      unit: value.unit as any,
    }
  }

  public async GetDataSet(fromState: string, toState: string) {
    const graphConfiguration = this.GetGraphConfiguration()

    let startDate = undefined
    let endDate = undefined

    if (graphConfiguration.startDate === 'month') {
      startDate = moment()
        .subtract(1, 'month')
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0)
        .toISOString()
    } else {
      startDate = moment(graphConfiguration.startDate).toISOString()
    }

    if (graphConfiguration.endDate === 'today') {
      endDate = moment()
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0)
        .toISOString()
    } else {
      endDate = moment(graphConfiguration.endDate).toISOString()
    }

    try {
      const analyticsData = await this.api.GetSalesLeadTime(
        this.organizationId,
        startDate,
        endDate,
        fromState,
        toState
      )

      this.DataWithTarget(analyticsData)

      return this.SalesLeadTimeData
    } catch (err) {
      this.SalesLeadTimeData = null
      this.LogError(err)
      throw err
    }
  }

  public GraphCachedDataWithNewTargets() {
    return this.DataWithTarget(this.SalesLeadTimeData)
  }

  private DataWithTarget(analyticsData: SalesLeadTimeDataset[]) {
    const targetInSeconds = moment
      .duration(
        this.configurationCache.value,
        this.configurationCache.unit as never
      )
      .asSeconds()

    const targetDataInit = {
      unixtimeinseconds: analyticsData[0].unixtimeinseconds,
      targetLeadTime: targetInSeconds,
    }

    const targetDataEnd = {
      unixtimeinseconds:
        analyticsData[analyticsData.length - 1].unixtimeinseconds,
      targetLeadTime: targetInSeconds,
    }

    if (
      Object.keys(analyticsData[analyticsData.length - 1]).includes(
        'targetLeadTime'
      )
    ) {
      analyticsData.splice(analyticsData.length - 2, 2)
    }

    const data = [
      ...analyticsData,
      targetDataInit,
      targetDataEnd,
    ] as SalesLeadTimeDataset[]

    this.SalesLeadTimeData = data

    return data
  }
}
